import { action, comparer, computed, IReactionDisposer, makeObservable, observable, reaction } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import type { GetProductFacetsQueryResponse, GetProductsByFamiliesQueryResponse } from '../../../app/data/model';
import { debounce } from '../../../app/shared/common';
import Logger from '../../../app/shared/Logger';
import { ICancellableTask, RunLastTasksScheduler } from '../../../app/shared/TasksScheduler';
import { RelativeUrl } from '../../../app/shared/Url';
import { SEARCH_DEBOUNCE_MS, type LoadingState } from '../../constants';
import { ProductListingTranslation } from '../../localization/ModuleTranslation';
import {
  SortDirection,
  SortField,
  type PricelistProductData,
  type PricelistProductFamilyData,
  type PricingContext,
  type ProductFiltersData,
  type ProductSortData,
} from '../../model';
import { ModuleRoutes } from '../../ModuleRoutes';
import { GetProductFacetsQuery, GetProductsByFamiliesQuery } from '../../queries';
import AddProductsToPricelistModalState from '../../shared/AddProductsToPricelistModal/AddProductsToPricelistModalState';
import ExportPricelistToPdfModalState from '../../shared/ExportPricelistToPdfModal/ExportPricelistToPdfModalState';
import { PRODUCT_COLUMN_IDS } from './constants';
import ExpansionState from './ExpansionState';
import { ProductFamilyDetailsState } from './ProductFamilyDetails/ProductFamilyDetailsState';
import { ProductListingFiltersState } from './ProductListingFilters/ProductListingFiltersState';
import { DESCENDING, EXPANDED_ALL, ProductListingQuery, ProductListingQueryParser } from './ProductListingQuery';
import ProductModelDetailsState from './ProductModelDetails/ProductModelDetailsState';
import ProductSelectionState from './ProductSelectionState';
import SortState from './SortState';

interface SearchProductsQueryParams {
  filters: ProductFiltersData;
  sort: ProductSortData;
}

/**
 * Families per request. Sized so one page outgrows the grid's scrollport plus the sentinel's
 * prefetch margin — a smaller page leaves the sentinel on screen and loads again immediately.
 */
const PAGE_SIZE = 30;

const SORT_FIELDS = {
  [PRODUCT_COLUMN_IDS.Brand]: SortField.BrandId,
  [PRODUCT_COLUMN_IDS.Model]: SortField.FamilyName,
  [PRODUCT_COLUMN_IDS.Price]: SortField.StartingPrice,
};

type SortableColumn = keyof typeof SORT_FIELDS;

const isSortableColumn = (column: string): column is SortableColumn => column in SORT_FIELDS;

const DEFAULT_SORT_COLUMN: SortableColumn = PRODUCT_COLUMN_IDS.Model;

/**
 * One request for a page of families, and whatever it brought back. `RunLastTasksScheduler` flags
 * the task `cancelled` as soon as a newer request starts, so once the run is awaited the caller
 * can tell an outcome that still matters from one a debounced reload has already superseded.
 */
class LoadProductsTask implements ICancellableTask {
  public cancelled = false;
  public response: GetProductsByFamiliesQueryResponse;
  /** Whatever the request rejected with — a `throw` is not obliged to carry an `Error`. */
  public error: unknown;

  public constructor(public readonly skip: number) {}
}

export default class ProductListingState {
  @observable.ref public details: ProductFamilyDetailsState | ProductModelDetailsState | null = null;
  @observable.ref public families: PricelistProductFamilyData[] = [];
  @observable.ref public filters: ProductListingFiltersState;
  @observable public loadingState: LoadingState = 'loading';
  @observable public searchTerm = '';
  @observable public total = 0;
  @observable public totalProducts = 0;

  public readonly expansion = new ExpansionState(() => this.families.map((family) => family.id));
  public readonly selection = new ProductSelectionState(() => this.families);
  public readonly sort: SortState;
  public readonly addToPricelistModal: AddProductsToPricelistModalState;

  public readonly exportToPdfModal: ExportPricelistToPdfModalState;

  public getDetailsRouteUrl = (product: PricelistProductData): RelativeUrl => this.routes.productDetails(product.id);

  private readonly urlQuery: ProductListingQuery;
  private readonly loadProductsScheduler: RunLastTasksScheduler<LoadProductsTask, void>;
  private disposeSearchReaction: IReactionDisposer;
  private isDisposed = false;

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    public readonly translation: ProductListingTranslation,
    private readonly routes: ModuleRoutes,
    query: Map<string, string> = new Map<string, string>(),
  ) {
    makeObservable(this);

    this.urlQuery = ProductListingQueryParser.toModel(query);

    const sortColumn = isSortableColumn(this.urlQuery.sort) ? this.urlQuery.sort : DEFAULT_SORT_COLUMN;
    const sortDirection = this.urlQuery.direction === DESCENDING ? 'descending' : 'ascending';

    this.filters = new ProductListingFiltersState(this.translation.filters);
    this.searchTerm = this.urlQuery.phrase;
    this.sort = new SortState(sortColumn, sortDirection);
    this.addToPricelistModal = new AddProductsToPricelistModalState(
      this.client,
      this.translation.addToPricelistModal,
      () => [...this.selection.selectedIds],
    );
    this.exportToPdfModal = new ExportPricelistToPdfModalState(this.client, this.pricingContext, () => ({
      productIds: [...this.selection.selectedIds],
      locale: this.pricingContext.languageCode,
    }));

    this.loadProductsScheduler = new RunLastTasksScheduler((task) => this.runLoadProductsTask(task));
  }

  @computed
  public get activeItemId(): string | undefined {
    return this.details?.data.id;
  }

  @computed
  public get canLoadMoreProducts(): boolean {
    if (this.loadingState === 'loading' || this.loadingState === 'error') {
      return false;
    }

    return this.families.length > 0 && this.families.length < this.total;
  }

  @computed
  public get productsCount(): number {
    return this.totalProducts;
  }

  public get currency(): string {
    return this.pricingContext.currency;
  }

  @computed
  public get query(): Map<string, string> {
    const { brands, types, sectors, priceFilters } = this.filters.criteria;
    const availablePriceRange = this.filters.priceRange.availableRange;

    return ProductListingQueryParser.toQuery({
      phrase: this.searchTerm,
      sectors,
      types,
      brands,
      priceMin: availablePriceRange.min === priceFilters?.min ? undefined : priceFilters?.min,
      priceMax: availablePriceRange.max === priceFilters?.max ? undefined : priceFilters?.max,
      sort: this.sort.column === DEFAULT_SORT_COLUMN ? undefined : this.sort.column,
      direction: this.sort.direction === 'descending' ? DESCENDING : undefined,
      expanded: this.expansion.areAllExpanded ? [EXPANDED_ALL] : [...this.expansion.expandedIds],
    });
  }

  @computed
  private get searchQueryParams(): SearchProductsQueryParams {
    return {
      filters: {
        searchPhrase: this.searchTerm,
        ...this.filters.criteria,
      },
      sort: {
        sortField: isSortableColumn(this.sort.column)
          ? SORT_FIELDS[this.sort.column]
          : SORT_FIELDS[DEFAULT_SORT_COLUMN],
        direction: this.sort.direction === 'descending' ? SortDirection.Descending : SortDirection.Ascending,
      },
    };
  }

  public async onLoad(): Promise<void> {
    const filters = await this.fetchFilters();

    this.initialize(filters);
  }

  public unload(): void {
    this.isDisposed = true;
    this.disposeSearchReaction?.();
    this.loadProductsScheduler.dispose();
  }

  @action.bound
  public async loadMoreProducts(): Promise<void> {
    if (!this.canLoadMoreProducts || this.loadingState === 'loadingMore') {
      return;
    }

    this.setLoadingState('loadingMore');

    const task = new LoadProductsTask(this.families.length);

    await this.loadProductsScheduler.run(task);

    if (task.cancelled) {
      return;
    }

    if (task.error) {
      Logger.warn(`Unable to load more products. ${task.error}`);
      this.setLoadingState('error');

      return;
    }

    if (task.response.productFamilies.empty()) {
      this.setTotal(this.families.length);
      this.setLoadingState('idle');

      return;
    }

    this.appendFamilies(task.response.productFamilies);
    this.setTotal(task.response.total);
    this.setTotalProducts(task.response.totalProducts);
    this.setLoadingState('idle');
  }

  @action.bound
  public setSearchTerm(searchTerm: string): void {
    this.searchTerm = searchTerm;
  }

  @action.bound
  public closeDetails(): void {
    this.details = null;
  }

  @action.bound
  public toggleDetails({ familyId, modelId }: { familyId: string; modelId?: string }): void {
    const family = this.families.find((f) => f.id === familyId);

    if (!family) {
      return;
    }

    if (!modelId) {
      this.details =
        family.id === this.activeItemId
          ? null
          : new ProductFamilyDetailsState(this.client, this.pricingContext, family);
      return;
    }

    const model = family.products.find((p) => p.id === modelId);

    if (!model) {
      return;
    }

    this.details =
      model.id === this.activeItemId
        ? null
        : new ProductModelDetailsState(this.client, this.pricingContext, this.routes, model, family);
  }

  @action
  private initialize(facets: GetProductFacetsQueryResponse): void {
    this.filters.setFilters(facets, {
      brands: this.urlQuery.brands,
      priceFilters: { min: this.urlQuery.priceMin, max: this.urlQuery.priceMax },
      sectors: this.urlQuery.sectors,
      types: this.urlQuery.types,
    });

    this.loadProducts();

    this.disposeSearchReaction = reaction(
      () => this.searchQueryParams,
      debounce(() => this.loadProducts(), SEARCH_DEBOUNCE_MS),
      { equals: comparer.structural },
    );
  }

  private async fetchFilters(): Promise<GetProductFacetsQueryResponse> {
    return this.client.send(
      new GetProductFacetsQuery({
        pricingContext: this.pricingContext,
      }),
    );
  }

  private async loadProducts(): Promise<void> {
    // Disposing stops the reaction but cannot clear a debounced reload it has already scheduled,
    // so the last one can still land on a page the user has left.
    if (this.isDisposed) {
      return;
    }

    const expandedFromUrl = this.families.empty() ? this.urlQuery.expanded : [];
    const shouldExpandAll = expandedFromUrl[0] === EXPANDED_ALL || this.expansion.areAllExpanded;
    const idsToExpand = shouldExpandAll ? [] : expandedFromUrl;
    const task = new LoadProductsTask(0);

    this.setLoadingState('loading');

    await this.loadProductsScheduler.run(task);

    if (task.cancelled) {
      return;
    }

    if (task.error) {
      Logger.error(`Unable to load the product listing. Error: ${task.error}`);
      this.setFamilies([]);
      this.setTotal(0);
      this.setTotalProducts(0);
      this.setLoadingState('error');

      return;
    }

    this.setFamilies(task.response.productFamilies);
    this.setTotal(task.response.total);
    this.setTotalProducts(task.response.totalProducts);

    if (shouldExpandAll) {
      this.expansion.expandAll();
    } else {
      this.expansion.setExpanded(idsToExpand);
    }

    this.setLoadingState('idle');
  }

  private async runLoadProductsTask(task: LoadProductsTask): Promise<void> {
    try {
      task.response = await this.fetchProducts(task.skip);
    } catch (error) {
      task.error = error;
    }
  }

  private async fetchProducts(skip: number): Promise<GetProductsByFamiliesQueryResponse> {
    return this.client.send(
      new GetProductsByFamiliesQuery({
        pricingContext: this.pricingContext,
        ...this.searchQueryParams,
        skip,
        take: PAGE_SIZE,
      }),
    );
  }

  @action.bound
  private setLoadingState(loadingState: LoadingState): void {
    this.loadingState = loadingState;
  }

  @action.bound
  private setFamilies(families: PricelistProductFamilyData[]): void {
    this.families = families;
  }

  @action.bound
  private appendFamilies(families: PricelistProductFamilyData[]): void {
    const wereAllExpanded = this.expansion.areAllExpanded;

    this.families = [...this.families, ...families];

    if (wereAllExpanded) {
      this.expansion.expandAll();
    }
  }

  @action.bound
  private setTotal(total: number): void {
    this.total = total;
  }

  @action.bound
  private setTotalProducts(total: number): void {
    this.totalProducts = total;
  }
}
